import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { SalesActions } from "@/components/crm/SalesActions";
import type { Sheet } from "@/lib/exports/file";
import { SalesShell } from "@/components/crm/SalesShell";
import { EmptyState, SelectField, Stat } from "@/components/crm/parts";
import { ForecastBars } from "@/components/crm/ForecastBars";
import { useOrgMembers } from "@/lib/crm/useOrgMembers";
import { forecastVisual } from "@/lib/crm/visual-sources";
import { useSalesOwner } from "@/lib/crm/owner-filter";
import { useAccounts, useOpportunities, useSaveRecord, useStages } from "@/lib/crm/useCrm";
import { useQuarterStartMonth } from "@/lib/crm/useQuarterStart";
import {
  FORECAST_PERIODS,
  commitCoverage,
  forecastByCategory,
  forecastByClosePeriod,
  forecastByOwner,
  inWindow,
  periodWindow,
  type ForecastPeriod,
} from "@/lib/crm/forecast";
import {
  FORECAST_CATEGORIES,
  gbp,
  isOpen,
  labelOf,
  money,
  OPPORTUNITY_TYPES,
  probabilityOf,
  weightedValue,
  type Opportunity,
} from "@/lib/crm/types";

export const Route = createFileRoute("/_authenticated/sales/forecast")({
  head: () => ({
    meta: [
      { title: "Forecast — MyPriorities" },
      {
        name: "description",
        content:
          "Tag opportunities as Pipeline, Upside, Best case, Commit or Closed and compare gross against stage-weighted forecast.",
      },
      { property: "og:title", content: "Forecast — MyPriorities" },
      {
        property: "og:description",
        content: "Gross versus weighted forecast by category, owner and close period.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ForecastPage,
});

function ForecastPage() {
  return (
    <SalesShell
      title="Forecast"
      subtitle="Categorise every deal, then compare the gross number with the weighted one."
    >
      {({ orgId }) => <ForecastBoard orgId={orgId} />}
    </SalesShell>
  );
}

function ForecastBoard({ orgId }: { orgId: string }) {
  const stages = useStages(orgId);
  const opportunities = useOpportunities(orgId);
  const accounts = useAccounts(orgId);
  const { ownerLabel, ownerOptions } = useOrgMembers(orgId);
  const quarterStartMonth = useQuarterStartMonth();
  const save = useSaveRecord<{ forecast_category: string }>("crm_opportunities");

  const [period, setPeriod] = useState<ForecastPeriod>("this_quarter");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [openOnly, setOpenOnly] = useState(true);
  const [search, setSearch] = useState("");

  const { ownerId: ownerFilter } = useSalesOwner();
  const stageRows = stages.data ?? [];
  const accountRows = accounts.data ?? [];
  const accountName = (id: string | null) =>
    accountRows.find((a) => a.id === id)?.name ?? "No account";

  const window = useMemo(
    () => periodWindow(period, quarterStartMonth),
    [period, quarterStartMonth],
  );

  /** Everything inside the chosen close-date window and owner scope. */
  const scoped = useMemo(
    () =>
      (opportunities.data ?? []).filter((o) => {
        if (!inWindow(o.expected_close_date, window)) return false;
        if (ownerFilter !== "all" && o.owner_user_id !== ownerFilter) return false;
        if (openOnly && o.forecast_category !== "closed" && !isOpen(o, stageRows)) return false;
        return true;
      }),
    [opportunities.data, window, ownerFilter, openOnly, stageRows],
  );

  const categoryRows = useMemo(() => forecastByCategory(scoped, stageRows), [scoped, stageRows]);
  const coverage = useMemo(() => commitCoverage(categoryRows), [categoryRows]);
  const ownerRows = useMemo(() => forecastByOwner(scoped, stageRows), [scoped, stageRows]);

  const listed = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scoped
      .filter(
        (o) => categoryFilter === "all" || (o.forecast_category ?? "pipeline") === categoryFilter,
      )
      .filter((o) =>
        !q
          ? true
          : o.name.toLowerCase().includes(q) || accountName(o.account_id).toLowerCase().includes(q),
      )
      .sort((a, b) => weightedValue(b, stageRows) - weightedValue(a, stageRows));
  }, [scoped, categoryFilter, search, stageRows, accountRows]);

  const retag = (opp: Opportunity, forecast_category: string) => {
    void save
      .mutateAsync({ id: opp.id, values: { forecast_category } })
      .then(() => toast.success(`${opp.name} → ${labelOf(FORECAST_CATEGORIES, forecast_category)}`))
      .catch((e: unknown) =>
        toast.error(e instanceof Error ? e.message : "Could not update the category"),
      );
  };

  const exportSheets = (): Sheet[] => {
    const periodRows = forecastByClosePeriod(listed, stageRows);
    const total = (
      pick: (r: { gross: number; weighted: number; commit: number; count: number }) => number,
    ) => periodRows.reduce((s, r) => s + pick(r), 0);

    return [
      {
        name: "By category",
        rows: [
          ["Category", "Opportunities", "Gross", "Weighted", "Average probability %"],
          ...categoryRows.map((r) => [
            r.label,
            r.count,
            r.gross,
            r.weighted,
            Math.round(r.averageProbability),
          ]),
          [
            "Total",
            categoryRows.reduce((s, r) => s + r.count, 0),
            coverage.totalGross,
            coverage.totalWeighted,
            "",
          ],
        ],
        money: [2, 3],
        percent: [4],
      },
      {
        name: "By owner",
        rows: [
          ["Owner", "Opportunities", "Gross", "Weighted", "Commit + closed"],
          ...ownerRows.map((r) => [ownerLabel(r.ownerId), r.count, r.gross, r.weighted, r.commit]),
          [
            "Total",
            ownerRows.reduce((s, r) => s + r.count, 0),
            ownerRows.reduce((s, r) => s + r.gross, 0),
            ownerRows.reduce((s, r) => s + r.weighted, 0),
            ownerRows.reduce((s, r) => s + r.commit, 0),
          ],
        ],
        money: [2, 3, 4],
      },
      {
        name: "By close period",
        rows: [
          ["Close period", "Opportunities", "Gross", "Weighted", "Commit + closed"],
          ...periodRows.map((r) => [r.label, r.count, r.gross, r.weighted, r.commit]),
          [
            "Total",
            total((r) => r.count),
            total((r) => r.gross),
            total((r) => r.weighted),
            total((r) => r.commit),
          ],
        ],
        money: [2, 3, 4],
      },
      {
        name: "Opportunities",
        rows: [
          [
            "Opportunity",
            "Account",
            "Owner",
            "Forecast tag",
            "Stage",
            "Close date",
            "Close period",
            "Probability",
            "Gross",
            "Weighted",
            "Opportunity type",
            "Product",
            "Next action",
            "Next action date",
          ],
          ...listed.map((o) => [
            o.name,
            accountName(o.account_id),
            ownerLabel(o.owner_user_id),
            labelOf(FORECAST_CATEGORIES, o.forecast_category),
            stageRows.find((s) => s.id === o.stage_id)?.name ?? "",
            o.expected_close_date ?? "",
            o.expected_close_date ? o.expected_close_date.slice(0, 7) : "No close date",
            probabilityOf(o, stageRows),
            money(o.expected_value),
            weightedValue(o, stageRows),
            labelOf(OPPORTUNITY_TYPES, o.opportunity_type),
            o.product_name ?? "",
            o.next_action ?? "",
            o.next_action_date ?? "",
          ]),
          [
            "Total",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            listed.reduce((s, o) => s + money(o.expected_value), 0),
            listed.reduce((s, o) => s + weightedValue(o, stageRows), 0),
            "",
            "",
            "",
            "",
          ],
        ],
        money: [8, 9],
        percent: [7],
      },
    ];
  };

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 basis-full sm:basis-44">
          <SelectField
            label="Close period"
            value={period}
            onChange={(v) => setPeriod(v as ForecastPeriod)}
            options={FORECAST_PERIODS}
          />
        </div>
        <div className="min-w-0 basis-full sm:basis-44">
          <SelectField
            label="Category"
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={[{ value: "all", label: "All categories" }, ...FORECAST_CATEGORIES]}
          />
        </div>
        <Button
          className="shrink-0"
          variant={openOnly ? "default" : "outline"}
          onClick={() => setOpenOnly((v) => !v)}
        >
          {openOnly ? "Open + closed won" : "Everything"}
        </Button>
        <div className="min-w-0 flex-1 basis-full sm:basis-56">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search opportunities or accounts"
          />
        </div>
        <SalesActions
          exports={[{ label: "Forecast", baseName: "forecast", sheets: exportSheets }]}
          visual={() => forecastVisual(ownerRows, ownerLabel)}
          sourceNote="From your sales forecast"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Gross forecast"
          value={gbp(coverage.totalGross)}
          hint={`${scoped.length} opportunities${window ? ` closing ${window.start} → ${window.end}` : ""}`}
        />
        <Stat
          label="Weighted forecast"
          value={gbp(coverage.totalWeighted)}
          hint="Deal probability, else stage weighting"
        />
        <Stat
          label="Commit + closed"
          value={gbp(coverage.gross)}
          hint={`${gbp(coverage.weighted)} weighted`}
        />
        <Stat
          label="Weighted cover"
          value={
            coverage.totalGross > 0
              ? `${Math.round((coverage.totalWeighted / coverage.totalGross) * 100)}%`
              : "—"
          }
          hint="Weighted as a share of gross"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Gross vs weighted by category</CardTitle>
        </CardHeader>
        <CardContent>
          {coverage.totalGross === 0 ? (
            <EmptyState
              title="Nothing to forecast yet"
              body="Give your opportunities an expected value and close date, then tag them Pipeline through Commit."
            />
          ) : (
            <ForecastBars rows={categoryRows} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">By owner</CardTitle>
        </CardHeader>
        <CardContent>
          {ownerRows.length === 0 ? (
            <EmptyState title="No owners in scope" body="Widen the period or clear the filters." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="py-1.5 pr-3">Owner</th>
                    <th className="py-1.5 pr-3">Deals</th>
                    <th className="py-1.5 pr-3">Gross</th>
                    <th className="py-1.5 pr-3">Weighted</th>
                    <th className="py-1.5">Commit + closed</th>
                  </tr>
                </thead>
                <tbody>
                  {ownerRows.map((r) => (
                    <tr key={r.ownerId} className="border-t border-border">
                      <td className="py-1.5 pr-3">{ownerLabel(r.ownerId)}</td>
                      <td className="py-1.5 pr-3 tabular-nums">{r.count}</td>
                      <td className="py-1.5 pr-3 tabular-nums">{gbp(r.gross)}</td>
                      <td className="py-1.5 pr-3 tabular-nums">{gbp(r.weighted)}</td>
                      <td className="py-1.5 tabular-nums">{gbp(r.commit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tag your deals</CardTitle>
        </CardHeader>
        <CardContent>
          {listed.length === 0 ? (
            <EmptyState
              title="No opportunities in this view"
              body="Change the close period, owner or category filters."
            />
          ) : (
            <ul className="grid gap-2">
              {listed.map((o) => (
                <li
                  key={o.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card p-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{o.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {accountName(o.account_id)} · {ownerLabel(o.owner_user_id)} ·{" "}
                      {o.expected_close_date ?? "no close date"}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{gbp(o.expected_value)} gross</Badge>
                    <Badge variant="secondary">
                      {gbp(weightedValue(o, stageRows))} @ {probabilityOf(o, stageRows)}%
                    </Badge>
                    <div className="w-40">
                      <SelectField
                        label="Category"
                        value={o.forecast_category ?? "pipeline"}
                        onChange={(v) => retag(o, v)}
                        options={FORECAST_CATEGORIES}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
